import { Activity, BellRing, Building2, KeyRound, Radio, RefreshCw, Scale, Wallet, Wifi, WifiOff } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ButtonLabel } from "../../../components/ButtonLabel";
import { Spinner } from "../../../components/Spinner";
import { apiRequest } from "../../../lib/api";
import { formatTimestamp, money, relativeTime } from "../../../lib/format";
import { useInvalidateAll, useKuspitOverview } from "../../../lib/queries";
import type { ApiEnvelope, KuspitOverview, KuspitProbeResult } from "../../../types";
import { Feedback, KeyValues, PageError, Pill, StatTile, compactMoney, describeKuspitEvent, useAction } from "../shared";
import { KUSPIT_BASE, KuspitFrame } from "./KuspitShared";

export function AdminKuspitOverviewPage() {
  const overview = useKuspitOverview();
  return (
    <KuspitFrame title="Kuspit" subtitle="Conector, contratos, saldos y webhooks del proveedor de custodia.">
      {overview.isLoading && <div className="page-state"><Spinner label="Cargando" /></div>}
      {(overview.isError || (!overview.isLoading && !overview.data)) && <PageError title="No pudimos cargar el estado de Kuspit" retry={() => void overview.refetch()} />}
      {overview.data && <Body data={overview.data} updatedAt={overview.dataUpdatedAt} />}
    </KuspitFrame>
  );
}

function Body({ data, updatedAt }: { data: KuspitOverview; updatedAt: number }) {
  const { connector, lastProbe, identities, wallets, sync, notifications, calls24h, custody } = data;
  const errorRate = calls24h.total ? calls24h.errors / calls24h.total : 0;
  return (
    <div className="admin-stack">
      <section className={`connector-card ${connector.reachable ? "up" : "down"}`}>
        <div className="connector-main">
          <span className="connector-icon">{connector.reachable ? <Wifi size={22} aria-hidden="true" /> : <WifiOff size={22} aria-hidden="true" />}</span>
          <div>
            <span className="rail-title">Conector Kuspit</span>
            <h2>{connector.reachable ? `En línea · modo ${connector.mode === "live" ? "LIVE (sandbox real)" : connector.mode === "mock" ? "MOCK (simulado)" : "desconocido"}` : "Sin respuesta"}</h2>
            <p className="field-hint">
              {connector.reachable ? `Respondió en ${connector.latencyMs} ms.` : "El API no pudo hablar con el conector; ninguna operación con Kuspit funcionará hasta que vuelva."}
              {lastProbe ? ` Última sonda del túnel ${lastProbe.ok ? "exitosa" : "fallida"} ${relativeTime(lastProbe.at)}.` : " Aún no hay sondas del túnel registradas."}
              {` Datos actualizados ${relativeTime(new Date(updatedAt).toISOString())}.`}
            </p>
          </div>
        </div>
        <ProbeButton />
      </section>

      <div className="stat-grid four">
        <StatTile label="Clientes vinculados" value={identities.linked} hint={`${identities.withContract} con contrato`} icon={KeyRound} to={`${KUSPIT_BASE}/contratos`} />
        <StatTile label="Tokens" value={identities.tokenValid} hint={`${identities.tokenExpiring} por renovar · ${identities.tokenExpired} vencidos`} icon={Radio} tone={identities.tokenExpired > 0 ? "alert" : "ok"} />
        <StatTile label="Wallets con contrato" value={`${wallets.withContract} / ${wallets.total}`} hint={`${wallets.withClabe} con CLABE · ${wallets.pending} en apertura`} icon={Wallet} to={`${KUSPIT_BASE}/contratos`} />
        <StatTile label="Esperando contrato" value={wallets.pendingStuck} hint="pendientes > 1 h o 3 intentos" icon={Building2} tone={wallets.pendingStuck > 0 ? "alert" : "ok"} to={`${KUSPIT_BASE}/contratos?solo=stuck`} />
      </div>

      <div className="admin-two-col">
        <section className="card">
          <div className="section-heading small"><div><span className="page-eyebrow">Saldos</span><h2>Lo que reporta Kuspit</h2></div><Link to={`${KUSPIT_BASE}/contratos`} className="text-link">Ver por wallet</Link></div>
          <div className="split-figures">
            <div><span>Saldo total en Kuspit</span><strong translate="no">{money.format(custody.providerTotal)}</strong><small>{custody.providerAsOf ? `última lectura ${formatTimestamp(custody.providerAsOf)}` : "sin lecturas aún"}</small></div>
            <div><span>Rendimientos acumulados</span><strong translate="no">{money.format(custody.interestTotal)}</strong><small>según Kuspit</small></div>
            <div className={custody.lockedTotal > 0.01 ? "bad" : "ok"}><span>Bloqueado</span><strong translate="no">{money.format(custody.lockedTotal)}</strong></div>
          </div>
          <p className="field-hint">El saldo del cliente es el que reporta Kuspit; no se compara con un saldo local. Aquí se ve si esa lectura está funcionando para cada wallet activa con contrato.</p>
          <KeyValues rows={[
            { label: "Wallets al día (< 30 min)", value: <Pill status="synced" label={String(sync.fresh)} /> },
            { label: "Sin saldo de Kuspit (última lectura falló)", value: <Link to={`${KUSPIT_BASE}/contratos?solo=failed`}><Pill status={sync.failed ? "failed" : "synced"} label={`${sync.failed}`} /></Link> },
            { label: "Desactualizadas (> 30 min)", value: <Link to={`${KUSPIT_BASE}/contratos?solo=stale`}><Pill status={sync.stale ? "stale" : "synced"} label={`${sync.stale}`} /></Link> },
            { label: "Nunca leídas", value: <Pill status={sync.never ? "never" : "synced"} label={`${sync.never}`} /> },
            { label: "Última lectura exitosa", value: sync.lastSyncedAt ? relativeTime(sync.lastSyncedAt) : "—" },
            { label: "Último intento", value: sync.lastAttemptAt ? relativeTime(sync.lastAttemptAt) : "—" },
            { label: "Instrucciones sin confirmar", value: `${custody.pendingInstructions}${custody.pendingInstructionsOld ? ` · ${custody.pendingInstructionsOld} con más de 1 h` : ""}` },
            { label: "Entrando / saliendo", value: `${compactMoney(custody.pendingIn)} / ${compactMoney(custody.pendingOut)}` }
          ]} />
        </section>

        <section className="card">
          <div className="section-heading small"><div><span className="page-eyebrow">Webhooks</span><h2>Notificaciones de Kuspit</h2></div><Link to={`${KUSPIT_BASE}/webhooks`} className="text-link">Ver todas</Link></div>
          <div className="pill-row">
            <Pill status="received" label={`${notifications.byStatus.received} en cola`} />
            <Pill status="processed" label={`${notifications.byStatus.processed} aplicadas`} />
            <Pill status="ignored" label={`${notifications.byStatus.ignored} ignoradas`} />
            <Pill status="failed" label={`${notifications.byStatus.failed} fallidas`} />
          </div>
          <p className="field-hint">{notifications.last24h} recibidas en 24 h · última {notifications.lastReceivedAt ? relativeTime(notifications.lastReceivedAt) : "nunca"}.</p>
          {notifications.byEvent.length > 0 && (
            <div className="table-wrap flat">
              <table className="admin-table compact">
                <thead><tr><th>Evento</th><th>Total</th><th>Fallidas</th></tr></thead>
                <tbody>
                  {notifications.byEvent.map((row) => (
                    <tr key={row.code}>
                      <td><Link to={`${KUSPIT_BASE}/webhooks?evento=${row.code}`} className="table-primary"><strong>{describeKuspitEvent(row.code)}</strong></Link></td>
                      <td>{row.count}</td>
                      <td>{row.failed ? <Pill status="failed" label={String(row.failed)} /> : "0"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <section className="card">
        <div className="section-heading small"><div><span className="page-eyebrow">Conector</span><h2>Llamadas a Kuspit · 24 h</h2></div><Link to={`${KUSPIT_BASE}/llamadas`} className="text-link">Ver bitácora</Link></div>
        <div className="stat-grid four inset">
          <StatTile compact label="Llamadas" value={calls24h.total} icon={Activity} />
          <StatTile compact label="Errores" value={calls24h.errors} hint={`${(errorRate * 100).toFixed(0)}% del total`} icon={BellRing} tone={errorRate > 0.2 ? "danger" : errorRate > 0.05 ? "alert" : "ok"} />
          <StatTile compact label="Latencia mediana" value={calls24h.p50Ms === null ? "—" : `${calls24h.p50Ms} ms`} icon={RefreshCw} />
          <StatTile compact label="Latencia p95" value={calls24h.p95Ms === null ? "—" : `${calls24h.p95Ms} ms`} icon={Scale} tone={(calls24h.p95Ms ?? 0) > 8000 ? "alert" : "default"} />
        </div>
        {calls24h.byEndpoint.length > 0 && (
          <div className="table-wrap flat">
            <table className="admin-table compact">
              <thead><tr><th>Endpoint interno</th><th>Llamadas</th><th>OK</th><th>Errores</th><th>p95</th><th>Última</th></tr></thead>
              <tbody>
                {calls24h.byEndpoint.map((row) => (
                  <tr key={row.endpoint}>
                    <td className="mono">{row.endpoint.replace("/internal/v1/", "")}</td>
                    <td>{row.total}</td>
                    <td>{row.ok}</td>
                    <td>{row.errors ? <Pill status="failed" label={String(row.errors)} /> : "0"}</td>
                    <td>{row.p95Ms === null ? "—" : `${row.p95Ms} ms`}</td>
                    <td>{relativeTime(row.lastAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

/** Runs the tunnel probe on demand and shows what came back, target by target. */
function ProbeButton() {
  const action = useAction();
  const invalidate = useInvalidateAll();
  const [result, setResult] = useState<KuspitProbeResult | null>(null);
  const probe = () => action.run("probe", async () => {
    const response = await apiRequest<ApiEnvelope<KuspitProbeResult>>("/api/v1/admin/kuspit/probe", { method: "POST" });
    setResult(response.data);
    await invalidate();
    if (response.data.error) return `El conector respondió, pero la sonda falló: ${response.data.error}.`;
    if (!response.data.probe) return "Sonda completada.";
    return response.data.probe.mode === "mock" ? "Conector en modo simulado: no hay túnel que probar." : `Túnel probado: ${response.data.probe.reachable} de ${response.data.probe.checked} destinos alcanzables.`;
  });
  return (
    <div className="connector-actions">
      <button type="button" className="secondary-button small" onClick={() => void probe()} disabled={action.busy !== null}>
        <ButtonLabel busy={action.busy === "probe"} busyLabel="Probando" label="Probar túnel ahora" icon={<Radio size={14} aria-hidden="true" />} iconFirst />
      </button>
      <Feedback error={action.error} notice={action.notice} />
      {result?.probe && result.probe.targets.length > 0 && (
        <ul className="probe-targets">
          {result.probe.targets.map((target) => (
            <li key={`${target.host}:${target.port}`}>
              <Pill tone={target.reachable ? "ok" : "bad"} label={target.reachable ? "alcanzable" : "sin respuesta"} />
              <span className="mono">{target.host}:{target.port}</span>
              <span>{target.reachable ? `${target.latencyMs} ms` : target.failure ?? ""}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
